import { useState, useEffect } from 'react';
import { ChevronLeft, CreditCard, CheckCircle, Zap, Crown, Star, ExternalLink, PartyPopper, XCircle, Loader2 } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

// Stripe configuration from environment
const STRIPE_PRO_PAYMENT_LINK = import.meta.env.VITE_STRIPE_PRO_PAYMENT_LINK || '';
const STRIPE_TEAM_PAYMENT_LINK = import.meta.env.VITE_STRIPE_TEAM_PAYMENT_LINK || '';
const STRIPE_CUSTOMER_PORTAL_LINK = import.meta.env.VITE_STRIPE_CUSTOMER_PORTAL_LINK || '';

interface PlanFeature {
    text: string;
    included: boolean;
}

interface Plan {
    id: string;
    name: string;
    price: string;
    period: string;
    description: string;
    features: PlanFeature[];
    popular?: boolean;
    paymentLink?: string;
}

const plans: Plan[] = [
    {
        id: 'free',
        name: 'Free',
        price: '$0',
        period: 'forever',
        description: 'Get started with basic productivity features',
        features: [
            { text: 'Up to 50 tasks', included: true },
            { text: 'Basic voice capture', included: true },
            { text: 'Calendar view', included: true },
            { text: 'AI task suggestions', included: false },
            { text: 'Integrations', included: false },
            { text: 'Priority support', included: false },
        ],
    },
    {
        id: 'pro',
        name: 'Pro',
        price: '$9.99',
        period: '/month',
        description: 'Unlock the full power of AI productivity',
        popular: true,
        paymentLink: STRIPE_PRO_PAYMENT_LINK,
        features: [
            { text: 'Unlimited tasks', included: true },
            { text: 'Advanced voice AI', included: true },
            { text: 'All integrations', included: true },
            { text: 'AI scheduling & insights', included: true },
            { text: 'Morning briefings', included: true },
            { text: 'Priority support', included: true },
        ],
    },
    {
        id: 'team',
        name: 'Team',
        price: '$19.99',
        period: '/user/month',
        description: 'Perfect for teams and organizations',
        paymentLink: STRIPE_TEAM_PAYMENT_LINK,
        features: [
            { text: 'Everything in Pro', included: true },
            { text: 'Team collaboration', included: true },
            { text: 'Shared projects', included: true },
            { text: 'Admin controls', included: true },
            { text: 'API access', included: true },
            { text: 'Dedicated support', included: true },
        ],
    },
];

export default function AppSubscriptionBilling() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const { user } = useAuth();
    const currentPlan = user?.plan || 'free';

    // Check for success/canceled query params from Stripe redirect
    const [showSuccess, setShowSuccess] = useState(false);
    const [showCanceled, setShowCanceled] = useState(false);
    const [isRedirecting, setIsRedirecting] = useState(false);

    useEffect(() => {
        if (searchParams.get('success') === 'true') {
            setShowSuccess(true);
            // Clear the query params
            setSearchParams({});
        }
        if (searchParams.get('canceled') === 'true') {
            setShowCanceled(true);
            // Clear the query params
            setSearchParams({});
        }
    }, [searchParams, setSearchParams]);

    const handleUpgrade = (plan: Plan) => {
        if (!plan.paymentLink) {
            // Fallback to email if no payment link configured
            window.location.href = `mailto:support@way2will.com?subject=Upgrade%20to%20${plan.name}%20Plan&body=I'd%20like%20to%20upgrade%20to%20the%20${plan.name}%20plan.`;
            return;
        }

        // Check if payment link is properly configured
        if (plan.paymentLink.includes('YOUR_')) {
            alert('Stripe Payment Links are not configured yet. Please set up your Stripe Dashboard and add the Payment Link URLs to your environment variables.');
            return;
        }

        setIsRedirecting(true);

        // Add customer email to the payment link if available
        const emailParam = user?.email ? `?prefilled_email=${encodeURIComponent(user.email)}` : '';
        window.location.href = plan.paymentLink + emailParam;
    };

    const handleManageBilling = () => {
        if (!STRIPE_CUSTOMER_PORTAL_LINK || STRIPE_CUSTOMER_PORTAL_LINK.includes('YOUR_')) {
            // Fallback to email if no portal link configured
            window.location.href = 'mailto:support@way2will.com?subject=Billing%20Inquiry';
            return;
        }

        setIsRedirecting(true);
        window.location.href = STRIPE_CUSTOMER_PORTAL_LINK;
    };

    const isStripeConfigured = STRIPE_PRO_PAYMENT_LINK && !STRIPE_PRO_PAYMENT_LINK.includes('YOUR_');

    return (
        <div className="px-4 pb-8">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <button
                    onClick={() => navigate(-1)}
                    className="w-10 h-10 flex items-center justify-center rounded-full bg-[#F5F8FA] dark:bg-dark-elevated"
                >
                    <ChevronLeft className="w-5 h-5 text-[#4A5C6A]" />
                </button>
                <h1 className="text-xl font-bold text-[#1A2832] dark:text-white">Subscription & Billing</h1>
            </div>

            {/* Success Message */}
            {showSuccess && (
                <div className="bg-[#D5F2EA] rounded-2xl p-4 mb-6 flex items-start gap-3">
                    <PartyPopper className="w-6 h-6 text-[#3DA58A] flex-shrink-0" />
                    <div>
                        <h3 className="font-semibold text-[#3DA58A] mb-1">Welcome to Pro!</h3>
                        <p className="text-sm text-[#3DA58A]/80">
                            Your subscription is now active. Enjoy all the premium features!
                        </p>
                        <button
                            onClick={() => setShowSuccess(false)}
                            className="text-sm font-medium text-[#3DA58A] underline mt-2"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Canceled Message */}
            {showCanceled && (
                <div className="bg-[#FFF3E0] rounded-2xl p-4 mb-6 flex items-start gap-3">
                    <XCircle className="w-6 h-6 text-[#E65100] flex-shrink-0" />
                    <div>
                        <h3 className="font-semibold text-[#E65100] mb-1">Checkout Canceled</h3>
                        <p className="text-sm text-[#E65100]/80">
                            No worries! You can upgrade anytime when you're ready.
                        </p>
                        <button
                            onClick={() => setShowCanceled(false)}
                            className="text-sm font-medium text-[#E65100] underline mt-2"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Stripe Not Configured Warning (only in dev) */}
            {!isStripeConfigured && (
                <div className="bg-[#FFF9E6] border border-[#FFD700] rounded-2xl p-4 mb-6">
                    <h3 className="font-semibold text-[#B8860B] mb-1">⚠️ Stripe Not Configured</h3>
                    <p className="text-sm text-[#B8860B]/80">
                        Add your Stripe Payment Links to the environment variables to enable real payments.
                    </p>
                </div>
            )}

            {/* Current Plan Banner */}
            <div className="bg-gradient-to-r from-[#0D5F6B] to-[#2A7A85] rounded-2xl p-4 mb-6">
                <div className="flex items-center gap-3 mb-2">
                    <Crown className="w-6 h-6 text-[#FFD700]" />
                    <span className="text-lg font-semibold text-white">
                        Current Plan: {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
                    </span>
                </div>
                <p className="text-white/80 text-sm mb-3">
                    {currentPlan === 'free'
                        ? 'Upgrade to unlock AI features and integrations'
                        : currentPlan === 'pro'
                            ? 'You have access to all Pro features'
                            : 'You have access to all Team features'}
                </p>
                {currentPlan !== 'free' && (
                    <div className="flex gap-2">
                        <button
                            onClick={handleManageBilling}
                            disabled={isRedirecting}
                            className="px-4 py-2 bg-white text-[#0D5F6B] rounded-xl font-semibold text-sm flex items-center gap-2 disabled:opacity-50"
                        >
                            {isRedirecting ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <CreditCard className="w-4 h-4" />
                            )}
                            Manage Billing
                        </button>
                    </div>
                )}
            </div>

            {/* Billing Info (for paid users) */}
            {currentPlan !== 'free' && (
                <div className="bg-white dark:bg-dark-card rounded-2xl p-4 mb-6 shadow-sm">
                    <h2 className="text-base font-semibold text-[#1A2832] dark:text-white mb-3">Billing Details</h2>
                    <p className="text-sm text-[#7B8C9B] mb-3">
                        Manage your subscription, update payment methods, and view invoices in the Stripe Customer Portal.
                    </p>
                    <button
                        onClick={handleManageBilling}
                        disabled={isRedirecting}
                        className="text-[#0D5F6B] font-medium text-sm flex items-center gap-1"
                    >
                        Open Billing Portal <ExternalLink className="w-4 h-4" />
                    </button>
                </div>
            )}

            {/* Plans */}
            <h2 className="text-base font-semibold text-[#1A2832] dark:text-white mb-3">Available Plans</h2>
            <div className="space-y-4 mb-6">
                {plans.map((plan) => (
                    <div
                        key={plan.id}
                        className={`bg-white dark:bg-dark-card rounded-2xl p-4 shadow-sm relative ${plan.popular ? 'ring-2 ring-[#0D5F6B]' : ''
                            } ${currentPlan === plan.id ? 'bg-[#F5FCFD]' : ''}`}
                    >
                        {plan.popular && (
                            <div className="absolute -top-3 left-4">
                                <span className="px-3 py-1 bg-[#0D5F6B] text-white text-xs font-semibold rounded-full flex items-center gap-1">
                                    <Star className="w-3 h-3" />
                                    Most Popular
                                </span>
                            </div>
                        )}

                        <div className="flex items-start justify-between mb-3 mt-1">
                            <div>
                                <h3 className="text-lg font-bold text-[#1A2832] dark:text-white flex items-center gap-2">
                                    {plan.name}
                                    {currentPlan === plan.id && (
                                        <span className="px-2 py-0.5 bg-[#D5F2EA] text-[#3DA58A] text-xs font-semibold rounded-full">
                                            Current
                                        </span>
                                    )}
                                </h3>
                                <p className="text-xs text-[#7B8C9B]">{plan.description}</p>
                            </div>
                            <div className="text-right">
                                <span className="text-2xl font-bold text-[#1A2832] dark:text-white">{plan.price}</span>
                                <span className="text-sm text-[#7B8C9B]">{plan.period}</span>
                            </div>
                        </div>

                        <div className="space-y-2 mb-4">
                            {plan.features.map((feature, index) => (
                                <div key={index} className="flex items-center gap-2">
                                    {feature.included ? (
                                        <CheckCircle className="w-4 h-4 text-[#3DA58A]" />
                                    ) : (
                                        <div className="w-4 h-4 rounded-full border-2 border-[#DCE4EA]" />
                                    )}
                                    <span className={`text-sm ${feature.included ? 'text-[#1A2832] dark:text-white' : 'text-[#7B8C9B] line-through'}`}>
                                        {feature.text}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {currentPlan !== plan.id && (
                            <button
                                onClick={() => handleUpgrade(plan)}
                                disabled={isRedirecting || plan.id === 'free'}
                                className={`w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2 ${plan.id === 'free'
                                    ? 'border border-[#DCE4EA] text-[#7B8C9B] cursor-not-allowed'
                                    : 'bg-[#0D5F6B] text-white hover:bg-[#0A4E59] transition disabled:opacity-50'
                                    }`}
                            >
                                {isRedirecting ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : plan.id === 'free' ? (
                                    'Contact Support to Downgrade'
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4" />
                                        Upgrade to {plan.name}
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                ))}
            </div>

            {/* FAQ Link */}
            <div className="text-center">
                <button
                    onClick={() => navigate('/app/help')}
                    className="text-sm text-[#0D5F6B] font-medium flex items-center gap-1 mx-auto"
                >
                    View Billing FAQ <ExternalLink className="w-4 h-4" />
                </button>
            </div>
        </div>
    );
}
